/**
 * AktionszeitraeumePage — Forecast Aktionszeiträume Übersicht + Kalender.
 *
 * @page /forecast/aktionszeitraeume
 * @roles admin, ops_manager, controlling
 * @api /api/aktionszeitraeume, /api/aktionszeitraeume/kalender, /api/kunden
 * @i18n forecastPromo
 * @since v2.174.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarDays, List, Plus, ChevronLeft, ChevronRight, Zap, TrendingUp, CheckCircle2, Users, Clock, Check, X, AlertCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SharedForecastTabs from '../components/SharedForecastTabs';
import ConfirmDialog from '../components/ConfirmDialog';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const TYP_COLORS = {
  black_week: { bg: '#FEE2E2', text: '#DC2626', border: '#EF4444' },
  sale: { bg: '#FEF3C7', text: '#D97706', border: '#F59E0B' },
  launch: { bg: '#DBEAFE', text: '#2563EB', border: '#3B82F6' },
  kampagne: { bg: '#EDE9FE', text: '#7C3AED', border: '#8B5CF6' },
  saison: { bg: '#CFFAFE', text: '#0891B2', border: '#06B6D4' },
  sonstige: { bg: '#F3F4F6', text: '#4B5563', border: '#6B7280' },
};

const STATUS_COLORS = {
  eingereicht: { bg: '#FEF3C7', text: '#D97706' },
  genehmigt: { bg: '#DBEAFE', text: '#2563EB' },
  abgelehnt: { bg: '#FEE2E2', text: '#DC2626' },
  aktiv: { bg: '#D1FAE5', text: '#059669' },
  abgeschlossen: { bg: '#F3F4F6', text: '#6B7280' },
};

function fmtDate(d) {
  if (!d) return '–';
  const dt = new Date(d);
  return dt.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// #1764 — Diff zwischen aktuellem Aktionszeitraum und den beantragten Zielwerten.
// changes trägt IMMER die kompletten Zielwerte, daher wird feldweise verglichen.
function crDiff(cr, t) {
  const ch = cr.changes || {};
  const current = {
    name: cr.titel ?? null,
    type: cr.typ ?? null,
    date_from: cr.datum_von ? String(cr.datum_von).slice(0, 10) : null,
    date_to: cr.datum_bis ? String(cr.datum_bis).slice(0, 10) : null,
    multiplier: cr.multiplikator == null ? null : Number(cr.multiplikator),
    additional_orders: cr.zusatz_orders == null ? null : Number(cr.zusatz_orders),
    description: cr.beschreibung || null,
  };
  const fields = [
    { key: 'name', label: t('forecastPromo.titleField'), fmt: v => v || '–' },
    { key: 'type', label: t('forecastPromo.type'), fmt: v => v ? t(`forecastPromo.typ.${v}`) : '–' },
    { key: 'date_from', label: t('forecastPromo.from'), fmt: v => fmtDate(v) },
    { key: 'date_to', label: t('forecastPromo.to'), fmt: v => fmtDate(v) },
    { key: 'multiplier', label: t('forecastPromo.multiplier'), fmt: v => v == null ? '–' : `${v}x` },
    { key: 'additional_orders', label: t('forecastPromo.additionalOrders'), fmt: v => v == null ? '–' : `+${v}` },
    { key: 'description', label: t('forecastPromo.description'), fmt: v => v || '–' },
  ];
  return fields
    .filter(f => {
      const a = current[f.key] ?? null;
      const b = ch[f.key] ?? null;
      return String(a) !== String(b);
    })
    .map(f => ({ label: f.label, from: f.fmt(current[f.key] ?? null), to: f.fmt(ch[f.key] ?? null) }));
}

export default function AktionszeitraeumePage({ embedded = false }) {
  const { t } = useLanguage();
  const [view, setView] = useState('list'); // 'list' | 'calendar'
  const [data, setData] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', item?: ... }
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTyp, setFilterTyp] = useState('');
  const [filterKunde, setFilterKunde] = useState('');
  const [sortField, setSortField] = useState('datum_von');
  const [sortDir, setSortDir] = useState('desc');
  // Calendar state
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return d.getMonth(); });
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const [calData, setCalData] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [items, kundenList, crs] = await Promise.all([
        api('/api/aktionszeitraeume'),
        api('/api/kunden?nur_aktive=true'),
        api('/api/aktionszeitraeume/change-requests?status=pending'),
      ]);
      setData(items);
      setKunden(Array.isArray(kundenList) ? kundenList : (kundenList.kunden || []));
      setChangeRequests(Array.isArray(crs) ? crs : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadCalendar = useCallback(async () => {
    const von = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(calYear, calMonth + 1, 0).getDate();
    const bis = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    try {
      const items = await api(`/api/aktionszeitraeume/kalender?von=${von}&bis=${bis}`);
      setCalData(items);
    } catch (e) { console.error(e); }
  }, [calMonth, calYear]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (view === 'calendar') loadCalendar(); }, [view, loadCalendar]);

  // KPIs
  const kpis = useMemo(() => {
    const now = new Date().toISOString().slice(0, 10);
    const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
    const monthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10);
    return {
      active: data.filter(d => d.status === 'aktiv').length,
      pending: data.filter(d => d.status === 'eingereicht').length,
      approved: data.filter(d => d.status === 'genehmigt').length,
      affectedCustomers: new Set(data.filter(d => !['abgeschlossen','abgelehnt'].includes(d.status)).map(d => d.kunde_id)).size,
    };
  }, [data]);

  // Filtered + sorted list
  const filteredData = useMemo(() => {
    let items = [...data];
    if (filterStatus) items = items.filter(d => d.status === filterStatus);
    if (filterTyp) items = items.filter(d => d.typ === filterTyp);
    if (filterKunde) items = items.filter(d => String(d.kunde_id) === filterKunde);
    items.sort((a, b) => {
      const va = a[sortField] ?? '';
      const vb = b[sortField] ?? '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data, filterStatus, filterTyp, filterKunde, sortField, sortDir]);

  const showActionsColumn = useMemo(() => filteredData.some(d => d.status === 'eingereicht'), [filteredData]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, variant, confirmLabel, onConfirm }
  const [rejectModal, setRejectModal] = useState(null); // { id, titel }
  const [crRejectModal, setCrRejectModal] = useState(null); // Change-Request (#1764)

  const handleSave = async (formData) => {
    try {
      if (modal.mode === 'edit') {
        await api(`/api/aktionszeitraeume/${modal.item.id}`, { method: 'PUT', body: formData });
      } else {
        await api('/api/aktionszeitraeume', { method: 'POST', body: formData });
      }
      setModal(null);
      loadData();
    } catch (e) { /* silent */ }
  };

  const handleApprove = (id) => {
    setConfirmDialog({
      title: t('forecastPromo.approve'),
      message: t('forecastPromo.confirmApprove'),
      variant: 'primary',
      confirmLabel: t('forecastPromo.approve'),
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api(`/api/aktionszeitraeume/${id}/approve`, { method: 'PUT' });
          loadData();
        } catch (e) { /* silent */ }
      },
    });
  };

  const handleReject = async (grund) => {
    try {
      await api(`/api/aktionszeitraeume/${rejectModal.id}/reject`, { method: 'PUT', body: { grund } });
      setRejectModal(null);
      loadData();
    } catch (e) { alert(e.message); }
  };

  const handleCrApprove = (cr) => {
    const isDelete = cr.request_type === 'delete';
    setConfirmDialog({
      title: t('forecastPromo.approve'),
      message: isDelete ? t('forecastPromo.cr.confirmApproveDelete') : t('forecastPromo.cr.confirmApproveEdit'),
      variant: isDelete ? 'danger' : 'primary',
      confirmLabel: t('forecastPromo.approve'),
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api(`/api/aktionszeitraeume/change-requests/${cr.id}/approve`, { method: 'PUT' });
          loadData();
        } catch (e) { alert(e.message); }
      },
    });
  };

  const handleCrReject = async (grund) => {
    try {
      await api(`/api/aktionszeitraeume/change-requests/${crRejectModal.id}/reject`, { method: 'PUT', body: { grund } });
      setCrRejectModal(null);
      loadData();
    } catch (e) { alert(e.message); }
  };

  // Calendar helpers
  const calDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1; // Mon=0
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [calMonth, calYear]);

  const getActionsForDay = useCallback((day) => {
    if (!day) return [];
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calData.filter(a => a.datum_von <= dateStr && a.datum_bis >= dateStr);
  }, [calData, calMonth, calYear]);

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {!embedded && <SharedForecastTabs />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl" style={{ background: '#ECFEFF' }}>
            <CalendarDays size={20} style={{ color: '#22D3EE' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('forecastPromo.title')}</h1>
            <p style={{ color: 'var(--text-tertiary)' }} className="text-sm mt-1">{t('forecastPromo.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
          style={{ backgroundColor: '#338edc' }}
        >
          <Plus size={16} /> {t('forecastPromo.newPromo')}
        </button>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: t('forecastPromo.kpi.active'), value: kpis.active, icon: Zap, color: '#059669' },
          { label: t('forecastPromo.kpi.pending'), value: kpis.pending, icon: Clock, color: '#D97706' },
          { label: t('forecastPromo.kpi.approved'), value: kpis.approved, icon: CheckCircle2, color: '#338edc' },
          { label: t('forecastPromo.kpi.affectedCustomers'), value: kpis.affectedCustomers, icon: Users, color: '#8B5CF6' },
        ].map((kpi, i) => (
          <div key={i} className="rounded-xl p-4 border" style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon size={16} style={{ color: kpi.color }} />
              <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-[10px] p-[3px] border" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
          {[
            { key: 'list', label: t('forecastPromo.viewList'), icon: List },
            { key: 'calendar', label: t('forecastPromo.viewCalendar'), icon: CalendarDays },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-sm font-medium transition-all ${
                view === v.key ? 'bg-white shadow-sm' : ''
              }`}
              style={{ color: view === v.key ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
            >
              <v.icon size={14} /> {v.label}
            </button>
          ))}
        </div>

        {view === 'list' && (
          <div className="flex items-center gap-2 ml-4">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5"
              style={{ borderColor: 'var(--border-primary, #E5E7EB)', color: 'var(--text-secondary)' }}
            >
              <option value="">{t('forecastPromo.status')}: Alle</option>
              {['eingereicht', 'genehmigt', 'abgelehnt', 'aktiv', 'abgeschlossen'].map(s => (
                <option key={s} value={s}>{t(`forecastPromo.status.${s}`)}</option>
              ))}
            </select>
            <select
              value={filterTyp}
              onChange={e => setFilterTyp(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5"
              style={{ borderColor: 'var(--border-primary, #E5E7EB)', color: 'var(--text-secondary)' }}
            >
              <option value="">{t('forecastPromo.type')}: Alle</option>
              {Object.keys(TYP_COLORS).map(typ => (
                <option key={typ} value={typ}>{t(`forecastPromo.typ.${typ}`)}</option>
              ))}
            </select>
            <select
              value={filterKunde}
              onChange={e => setFilterKunde(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5"
              style={{ borderColor: 'var(--border-primary, #E5E7EB)', color: 'var(--text-secondary)' }}
            >
              <option value="">{t('forecastPromo.customer')}: Alle</option>
              {kunden.map(k => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Change-Request Card (#1764) — Änderungs-/Löschanträge aus Flow OMS */}
      {changeRequests.length > 0 && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#6366F1', backgroundColor: '#EEF2FF' }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={18} style={{ color: '#4F46E5' }} />
            <span className="text-sm font-semibold" style={{ color: '#3730A3' }}>
              {t('forecastPromo.cr.title', { count: changeRequests.length })}
            </span>
          </div>
          <div className="space-y-2">
            {changeRequests.map(cr => {
              const isDelete = cr.request_type === 'delete';
              const diff = isDelete ? [] : crDiff(cr, t);
              return (
                <div key={cr.id} className="bg-white rounded-lg p-3 border flex items-start gap-3" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        backgroundColor: isDelete ? '#FEE2E2' : '#EDE9FE',
                        color: isDelete ? '#DC2626' : '#7C3AED',
                      }}>
                        {isDelete ? t('forecastPromo.cr.deleteRequest') : t('forecastPromo.cr.editRequest')}
                      </span>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {cr.kunde_name} — {cr.titel || cr.promotion_external_id}
                      </span>
                    </div>
                    {cr.aktionszeitraum_id ? (
                      <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                        {fmtDate(cr.datum_von)} – {fmtDate(cr.datum_bis)} · {cr.multiplikator ? `${cr.multiplikator}x` : `+${cr.zusatz_orders}`}
                      </div>
                    ) : (
                      <div className="text-xs mt-1" style={{ color: '#DC2626' }}>{t('forecastPromo.cr.promoMissing')}</div>
                    )}
                    {cr.reason && (
                      <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                        <span className="font-medium">{t('forecastPromo.cr.reason')}: </span>{cr.reason}
                      </div>
                    )}
                    {isDelete ? (
                      <div className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>{t('forecastPromo.cr.deleteHint')}</div>
                    ) : diff.length > 0 ? (
                      <div className="mt-2 space-y-0.5">
                        {diff.map(d => (
                          <div key={d.label} className="flex items-center gap-2 text-xs">
                            <span className="font-medium w-36 shrink-0" style={{ color: 'var(--text-tertiary)' }}>{d.label}</span>
                            <span className="line-through" style={{ color: 'var(--text-secondary)' }}>{d.from}</span>
                            <ArrowRight size={12} style={{ color: 'var(--text-muted)' }} />
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{d.to}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>{t('forecastPromo.cr.noChanges')}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleCrApprove(cr)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white shrink-0"
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Check size={12} /> {t('forecastPromo.approve')}
                  </button>
                  <button
                    onClick={() => setCrRejectModal(cr)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border shrink-0"
                    style={{ color: '#DC2626', borderColor: '#FCA5A5' }}
                  >
                    <X size={12} /> {t('forecastPromo.reject')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending Approval Card */}
      {kpis.pending > 0 && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={18} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">
              {t('forecastPromo.pendingApproval', { count: kpis.pending })}
            </span>
          </div>
          <div className="space-y-2">
            {data.filter(d => d.status === 'eingereicht').map(item => {
              const typColor = TYP_COLORS[item.typ] || TYP_COLORS.sonstige;
              return (
                <div key={item.id} className="flex items-center gap-3 bg-white rounded-lg p-3 border" style={{ borderColor: '#E5E7EB' }}>
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: typColor.border }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.kunde_name} — {item.titel}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {fmtDate(item.datum_von)} – {fmtDate(item.datum_bis)} · {item.multiplikator ? `${item.multiplikator}x` : `+${item.zusatz_orders}`}
                      {item.quelle === 'portal' && <span className="ml-2 text-amber-600">{t('forecastPromo.portal')}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleApprove(item.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Check size={12} /> {t('forecastPromo.approve')}
                  </button>
                  <button
                    onClick={() => setRejectModal(item)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border"
                    style={{ color: '#DC2626', borderColor: '#FCA5A5' }}
                  >
                    <X size={12} /> {t('forecastPromo.reject')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
                {[
                  { key: 'kunde_name', label: t('forecastPromo.customer') },
                  { key: 'titel', label: t('forecastPromo.titleField') },
                  { key: 'typ', label: t('forecastPromo.type') },
                  { key: 'datum_von', label: t('forecastPromo.from') },
                  { key: 'datum_bis', label: t('forecastPromo.to') },
                  { key: 'multiplikator', label: t('forecastPromo.multiplier') },
                  { key: 'status', label: t('forecastPromo.status') },
                  { key: 'quelle', label: t('forecastPromo.source') },
                  { key: 'ist_multiplikator', label: t('forecastPromo.istVsPlan') },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-3 py-2 text-left font-medium cursor-pointer select-none"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    {col.label} {sortField === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
                {showActionsColumn && (
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--text-tertiary)' }}>
                    {t('forecastPromo.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={showActionsColumn ? 10 : 9} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>Loading...</td></tr>
              ) : filteredData.length === 0 ? (
                <tr><td colSpan={showActionsColumn ? 10 : 9} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>{t('forecastPromo.noEntries')}</td></tr>
              ) : filteredData.map(item => {
                const typColor = TYP_COLORS[item.typ] || TYP_COLORS.sonstige;
                const statusColor = STATUS_COLORS[item.status] || STATUS_COLORS.eingereicht;
                return (
                  <tr key={item.id} className="border-t" style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}>
                    <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--text-primary)' }}>{item.kunde_name}</td>
                    <td className="px-3 py-2.5" style={{ color: 'var(--text-secondary)' }}>{item.titel}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: typColor.bg, color: typColor.text }}>
                        {t(`forecastPromo.typ.${item.typ}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5" style={{ color: 'var(--text-secondary)' }}>{fmtDate(item.datum_von)}</td>
                    <td className="px-3 py-2.5" style={{ color: 'var(--text-secondary)' }}>{fmtDate(item.datum_bis)}</td>
                    <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.multiplikator ? `${item.multiplikator}x` : `+${item.zusatz_orders}`}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
                        {t(`forecastPromo.status.${item.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        backgroundColor: item.quelle === 'portal' ? '#FEF3C7' : '#F3F4F6',
                        color: item.quelle === 'portal' ? '#D97706' : '#4B5563',
                      }}>
                        {t(`forecastPromo.${item.quelle}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5" style={{ color: 'var(--text-secondary)' }}>
                      {item.bewertet && item.ist_multiplikator != null ? (
                        <span className={`font-medium ${parseFloat(item.ist_multiplikator) >= parseFloat(item.multiplikator || 1) ? 'text-green-600' : 'text-amber-600'}`}>
                          {item.ist_multiplikator}x
                          {item.multiplikator ? ` / ${item.multiplikator}x` : ''}
                        </span>
                      ) : '–'}
                    </td>
                    {showActionsColumn && (
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {item.status === 'eingereicht' && (
                            <>
                              <button onClick={() => handleApprove(item.id)} className="p-1.5 rounded-lg hover:bg-green-50 transition-colors" title={t('forecastPromo.approve')}>
                                <Check size={14} className="text-green-600" />
                              </button>
                              <button onClick={() => setRejectModal(item)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title={t('forecastPromo.reject')}>
                                <X size={14} className="text-red-500" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="border rounded-xl p-4" style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}>
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              <ChevronLeft size={20} style={{ color: 'var(--text-secondary)' }} />
            </button>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {new Date(calYear, calMonth).toLocaleDateString('de-AT', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              <ChevronRight size={20} style={{ color: 'var(--text-secondary)' }} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
              <div key={d} className="text-center text-xs font-medium py-1" style={{ color: 'var(--text-tertiary)' }}>{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {calDays.map((day, i) => {
              if (day === null) return <div key={`empty-${i}`} className="h-24" />;
              const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === todayStr;
              const actions = getActionsForDay(day);
              const jsDay = new Date(calYear, calMonth, day).getDay();
              const isWeekend = jsDay === 0 || jsDay === 6;

              return (
                <div
                  key={day}
                  className={`h-24 rounded-lg border p-1 overflow-hidden ${isToday ? 'ring-2 ring-blue-400' : ''}`}
                  style={{
                    borderColor: 'var(--border-primary, #E5E7EB)',
                    backgroundColor: isWeekend ? 'var(--bg-inset, #F8F9FC)' : 'transparent',
                  }}
                >
                  <div className={`text-xs font-medium mb-0.5 ${isToday ? 'text-blue-600' : ''}`} style={{ color: isToday ? undefined : 'var(--text-secondary)' }}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {actions.slice(0, 3).map(a => {
                      const c = TYP_COLORS[a.typ] || TYP_COLORS.sonstige;
                      return (
                        <div
                          key={a.id}
                          className="text-[10px] leading-tight px-1 py-0.5 rounded truncate cursor-pointer"
                          style={{ backgroundColor: c.bg, color: c.text, borderLeft: `2px solid ${c.border}` }}
                          title={`${a.kunde_name}: ${a.titel} (${a.multiplikator ? a.multiplikator + 'x' : '+' + a.zusatz_orders})`}
                          onClick={() => setModal({ mode: 'edit', item: a })}
                        >
                          {a.kunde_name?.split(' ')[0]}: {a.titel}
                        </div>
                      );
                    })}
                    {actions.length > 3 && (
                      <div className="text-[10px] font-medium" style={{ color: 'var(--text-tertiary)' }}>+{actions.length - 3}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && (
        <PromoModal
          mode={modal.mode}
          item={modal.item}
          kunden={kunden}
          t={t}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <RejectModal
          item={rejectModal}
          t={t}
          onReject={handleReject}
          onClose={() => setRejectModal(null)}
        />
      )}

      {/* Change-Request Reject Modal (#1764) */}
      {crRejectModal && (
        <RejectModal
          item={{ kunde_name: crRejectModal.kunde_name, titel: crRejectModal.titel || crRejectModal.promotion_external_id }}
          title={t('forecastPromo.cr.rejectTitle')}
          t={t}
          onReject={handleCrReject}
          onClose={() => setCrRejectModal(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        variant={confirmDialog?.variant}
        confirmLabel={confirmDialog?.confirmLabel}
        cancelLabel={t('confirm.abbrechen')}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

function RejectModal({ item, t, title, onReject, onClose }) {
  const [grund, setGrund] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-xl w-full max-w-md p-6 border border-[var(--border-card,#E5E7EB)]" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{title || t('forecastPromo.rejectTitle')}</h2>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          {item.kunde_name}: "{item.titel}"
        </p>
        <textarea
          value={grund}
          onChange={e => setGrund(e.target.value)}
          placeholder={t('forecastPromo.rejectReason')}
          rows={3}
          className="w-full border rounded-lg px-3 py-2 text-sm mb-4 resize-none"
          style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary, #E5E7EB)' }}>
            {t('forecastPromo.cancel')}
          </button>
          <button
            onClick={async () => { setSaving(true); await onReject(grund); setSaving(false); }}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: '#DC2626', opacity: saving ? 0.6 : 1 }}
          >
            {t('forecastPromo.reject')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromoModal({ mode, item, kunden, t, onSave, onClose }) {
  const [form, setForm] = useState({
    kunde_id: item?.kunde_id || '',
    titel: item?.titel || '',
    typ: item?.typ || 'sonstige',
    datum_von: item?.datum_von?.slice(0, 10) || '',
    datum_bis: item?.datum_bis?.slice(0, 10) || '',
    multiplikator: item?.multiplikator || '',
    zusatz_orders: item?.zusatz_orders || '',
    beschreibung: item?.beschreibung || '',
  });
  const [useMulti, setUseMulti] = useState(item?.multiplikator ? true : !item?.zusatz_orders);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      multiplikator: useMulti ? parseFloat(form.multiplikator) || null : null,
      zusatz_orders: !useMulti ? parseInt(form.zusatz_orders) || null : null,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-xl w-full max-w-lg p-6 border border-[var(--border-card,#E5E7EB)]" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          {mode === 'edit' ? t('forecastPromo.editPromo') : t('forecastPromo.newPromo')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Kunde */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.customer')}</label>
            <select
              value={form.kunde_id}
              onChange={e => setForm(f => ({ ...f, kunde_id: e.target.value }))}
              required
              className="w-full border rounded-lg px-3 py-2 text-sm"
              style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              disabled={mode === 'edit'}
            >
              <option value="">-- {t('forecastPromo.customer')} --</option>
              {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          </div>

          {/* Titel + Typ */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.titleField')}</label>
              <input
                value={form.titel}
                onChange={e => setForm(f => ({ ...f, titel: e.target.value }))}
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.type')}</label>
              <select
                value={form.typ}
                onChange={e => setForm(f => ({ ...f, typ: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              >
                {Object.keys(TYP_COLORS).map(typ => (
                  <option key={typ} value={typ}>{t(`forecastPromo.typ.${typ}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Datum */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.from')}</label>
              <input
                type="date"
                value={form.datum_von}
                onChange={e => setForm(f => ({ ...f, datum_von: e.target.value }))}
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.to')}</label>
              <input
                type="date"
                value={form.datum_bis}
                onChange={e => setForm(f => ({ ...f, datum_bis: e.target.value }))}
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              />
            </div>
          </div>

          {/* Multiplier Toggle */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.multiplierOrAdditional')}</label>
            <div className="flex rounded-[10px] p-[3px] border mb-2" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
              <button
                type="button"
                onClick={() => setUseMulti(true)}
                className={`flex-1 px-3 py-1.5 rounded-[8px] text-sm font-medium transition-all ${useMulti ? 'bg-white shadow-sm' : ''}`}
                style={{ color: useMulti ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
              >
                {t('forecastPromo.multiplier')}
              </button>
              <button
                type="button"
                onClick={() => setUseMulti(false)}
                className={`flex-1 px-3 py-1.5 rounded-[8px] text-sm font-medium transition-all ${!useMulti ? 'bg-white shadow-sm' : ''}`}
                style={{ color: !useMulti ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
              >
                {t('forecastPromo.additionalOrders')}
              </button>
            </div>
            {useMulti ? (
              <input
                type="number"
                step="0.1"
                min="1"
                max="20"
                value={form.multiplikator}
                onChange={e => setForm(f => ({ ...f, multiplikator: e.target.value }))}
                placeholder="z.B. 3.0"
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              />
            ) : (
              <input
                type="number"
                min="1"
                value={form.zusatz_orders}
                onChange={e => setForm(f => ({ ...f, zusatz_orders: e.target.value }))}
                placeholder="z.B. 500"
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
              />
            )}
          </div>

          {/* Beschreibung */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{t('forecastPromo.description')}</label>
            <textarea
              value={form.beschreibung}
              onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
              style={{ borderColor: 'var(--border-primary, #E5E7EB)' }}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border"
              style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary, #E5E7EB)' }}
            >
              {t('forecastPromo.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ backgroundColor: '#338edc', opacity: saving ? 0.6 : 1 }}
            >
              {t('forecastPromo.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
